/**
 * Per-product Spark prefill registry.
 *
 * The website's Product cards link to `/spark?product=<slug>`; the
 * SparkPage looks up the slug here to pre-populate Step 1's name,
 * description, and domain. The 21 entries mirror
 * `website/src/data/products.ts` — keep the two in sync when
 * products are added or renamed (a CI structural-diff is the
 * MARKETPLACE-BACKEND-WIRING-SPEC §4.4 follow-up).
 */
import type { DomainType } from '@/api/types';

export interface SparkProductPrefill {
  slug: string;
  name: string;
  description: string;
  domain: DomainType;
  /** Segment slug (matches `ui/src/data/segments.ts`) the product
   *  belongs to. Used by Step 1's segment selector so the dropdown
   *  ALSO updates when ?product= is the prefill source — without
   *  this, only `name` + `description` + `domain` flow through and
   *  the segment selector stays at its placeholder. */
  segmentId: string;
}

export const PRODUCT_PREFILLS: Record<string, SparkProductPrefill> = {
  // ── Industry ───────────────────────────────────────────────────
  'gate-to-shelf': {
    slug: 'gate-to-shelf',
    name: 'Gate to Shelf',
    description:
      'Sign every quality check, every cold-chain reading, every supplier audit. Hand a regulator a proof — not a binder.',
    domain: 'Process',
    segmentId: 'industry',
  },
  'amar-reporter': {
    slug: 'amar-reporter',
    name: 'AMAR Reporter',
    description:
      'AMAR-grade reporting for medical device events. 48-hour clock, signed evidence, attested chain.',
    domain: 'Medical',
    segmentId: 'healthcare',
  },
  'research-shield': {
    slug: 'research-shield',
    name: 'Research Shield',
    description:
      'Stamp every dataset, every method, every figure. Defend your authorship with a timestamp the journal cannot dispute.',
    domain: 'Generic',
    segmentId: 'education',
  },
  'cyber-shield': {
    slug: 'cyber-shield',
    name: 'Cyber Shield',
    description:
      'Replace the SOC evidence chain with signatures. Pass NIST 800-61 audits without a single PDF stitched by hand.',
    domain: { Custom: 'cybersecurity' },
    segmentId: 'cybersecurity',
  },
  'legal-evidence': {
    slug: 'legal-evidence',
    name: 'Legal Evidence',
    description:
      'Court-grade chain of custody — every photo, every statement, every transfer signed.',
    domain: { Custom: 'legal' },
    segmentId: 'legal',
  },
  atlas: {
    slug: 'atlas',
    name: 'ATLAS GI',
    description:
      'Weak signal detection. Find what others miss before anyone else does.',
    domain: { Custom: 'ai' },
    segmentId: 'ai',
  },

  // ── Municipal Mirrors ──────────────────────────────────────────
  'citizen-mirror': {
    slug: 'citizen-mirror',
    name: 'Citizen Mirror',
    description:
      'Council votes, FOIA responses, budget lines — all signed, all timestamped, all visible.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-procurement': {
    slug: 'mirror-procurement',
    name: 'Procurement Mirror',
    description: 'Every tender step, signed at the moment it happens.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-tender-exemption': {
    slug: 'mirror-tender-exemption',
    name: 'Tender Exemption Mirror',
    description: 'Exemption committees, fully recorded — comptroller-ready.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-council-decision': {
    slug: 'mirror-council-decision',
    name: 'Council Decision Mirror',
    description: 'Roll calls, motions, amendments — signed before adjournment.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-section-203': {
    slug: 'mirror-section-203',
    name: 'Section 203 Mirror',
    description:
      'Mayor + Treasurer dual-signature for Section 203 commitments, captured at issuance.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-privacy-compliance': {
    slug: 'mirror-privacy-compliance',
    name: 'Privacy Compliance Mirror',
    description:
      'Amendment 13 — every processing event signed and audit-ready.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-dpo': {
    slug: 'mirror-dpo',
    name: 'DPO Mirror',
    description: 'Every Data Protection Officer action, on the record.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-cyber-incident': {
    slug: 'mirror-cyber-incident',
    name: 'Cyber Incident Mirror',
    description:
      'Detection time, classification, containment, notification — signed in the moment.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-foi-response': {
    slug: 'mirror-foi-response',
    name: 'FOI Response Mirror',
    description: 'Every Freedom of Information request answered on the clock.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-state-comptroller': {
    slug: 'mirror-state-comptroller',
    name: 'State Comptroller Mirror',
    description: 'Comptroller queries, answered with a signed chain.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-service-ticket': {
    slug: 'mirror-service-ticket',
    name: 'Service Ticket Mirror',
    description: 'Every 106 ticket, signed at intake; SLA timestamps logged.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-permit': {
    slug: 'mirror-permit',
    name: 'Permit Mirror',
    description:
      'Business permits — every department review signed in turn, applicant sees the trail.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-building-permit': {
    slug: 'mirror-building-permit',
    name: 'Building Permit Mirror',
    description:
      'Plans, objections, hearings, conditions, final permit — every committee step signed.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-parking-enforcement': {
    slug: 'mirror-parking-enforcement',
    name: 'Parking Enforcement Mirror',
    description:
      'Officer name, time, location, vehicle, photo — bound into a signed citation.',
    domain: 'Government',
    segmentId: 'government',
  },
  'mirror-welfare-decision': {
    slug: 'mirror-welfare-decision',
    name: 'Welfare Decision Mirror',
    description:
      'Eligibility, allowances, appeals — each decision signed by the social worker and supervisor.',
    domain: 'Government',
    segmentId: 'government',
  },

  // ── Construction ───────────────────────────────────────────────
  'construction-permit': {
    slug: 'construction-permit',
    name: 'Construction Permit',
    description:
      'Every building permit and milestone signed at issuance — contractor licence, expiry, safety-plan hash, and each foundation/structure/roofing approval.',
    domain: 'Process',
    segmentId: 'construction',
  },
  'construction-inspection': {
    slug: 'construction-inspection',
    name: 'Construction Inspection',
    description:
      'Every site inspection signed by the certified inspector — PPE compliance, SII material certs, deficiency tracking, full open-to-close chain.',
    domain: 'Process',
    segmentId: 'construction',
  },

  // ── HR ─────────────────────────────────────────────────────────
  'hr-contract': {
    slug: 'hr-contract',
    name: 'HR Contract',
    description:
      'Every employment contract signed at issuance — type, salary floor, GDPR Article 6 basis, statutory notice, and 42 h/week ceiling.',
    domain: 'Process',
    segmentId: 'hr',
  },
  'hr-payroll': {
    slug: 'hr-payroll',
    name: 'HR Payroll',
    description:
      'Every payslip signed at generation — minimum wage, overtime, deduction cap, Bituach Leumi, income tax, ITA deduction file number.',
    domain: 'Process',
    segmentId: 'hr',
  },

  // ── Real Estate ────────────────────────────────────────────────
  'realestate-deed': {
    slug: 'realestate-deed',
    name: 'Real Estate Deed',
    description:
      'Every property transfer signed at execution — gush-chelka, parties, value, mas rechisha clearance, Tabu submission packet.',
    domain: 'Process',
    segmentId: 'real-estate',
  },
  'realestate-rental': {
    slug: 'realestate-rental',
    name: 'Real Estate Rental',
    description:
      'Every rental agreement and deposit signed — Fair Rental Law §25 cap, return conditions, every collection event timestamped.',
    domain: 'Process',
    segmentId: 'real-estate',
  },

  // ── Video production ───────────────────────────────────────────
  'video-from-script': {
    slug: 'video-from-script',
    name: 'Video From Script',
    description:
      'OpenAI → ElevenLabs → Runway. Signed script, signed audio, signed video — one hash, every frame provable.',
    domain: { Custom: 'creative' },
    segmentId: 'video',
  },
  'promo-video': {
    slug: 'promo-video',
    name: 'Promo Video',
    description:
      '30-second product promo: signed script + voiceover + Stability AI visuals + Runway assembly. One verifier URL per ad.',
    domain: { Custom: 'creative' },
    segmentId: 'video',
  },
  'explainer-video': {
    slug: 'explainer-video',
    name: 'Explainer Video',
    description:
      '60/90/120-second explainer with signed script, narration, illustrations, animation. Compliance teams resolve a single hash.',
    domain: { Custom: 'creative' },
    segmentId: 'video',
  },
  'signed-voiceover': {
    slug: 'signed-voiceover',
    name: 'Signed Voiceover',
    description:
      'ElevenLabs voiceover with an Ed25519 attestation binding audio + transcript + voice id + timestamp. Defends against deepfake claims.',
    domain: { Custom: 'creative' },
    segmentId: 'video',
  },
  'thumbnail-generator': {
    slug: 'thumbnail-generator',
    name: 'Thumbnail Generator',
    description:
      'Stability AI thumbnail with a signed binding to the title + style + dimensions. Five presets each for style and resolution.',
    domain: { Custom: 'creative' },
    segmentId: 'video',
  },
};

/**
 * Starter → product mapping. When a segment-card starter has a
 * dedicated commercial product, clicking the starter routes to
 * `/spark?product=<slug>` instead of `?starter=<id>` so the user
 * lands on the product's prefill (richer description, the
 * "Built by Growing Intelligence" detail page is one click away).
 *
 * Keys are `<segment-id>:<starter-id>` to avoid collisions across
 * segments that share starter ids (e.g. `inspection_report`
 * appears under construction AND real-estate).
 *
 * Only the construction / hr / real-estate trios are wired today;
 * other segments fall through to the standard starter prefill flow.
 */
export const STARTER_TO_PRODUCT: Record<string, string> = {
  // Construction
  'construction:permit_record': 'construction-permit',
  'construction:inspection_report': 'construction-inspection',
  'construction:safety_checklist': 'construction-inspection',
  'construction:material_cert': 'construction-inspection',
  'construction:progress_report': 'construction-permit',
  // HR
  'hr:offer_letter': 'hr-contract',
  'hr:employment_verify': 'hr-contract',
  'hr:training_cert': 'hr-contract',
  'hr:performance_review': 'hr-contract',
  'hr:termination_record': 'hr-payroll',
  // Real estate
  'real-estate:deed_proof': 'realestate-deed',
  'real-estate:appraisal_record': 'realestate-deed',
  'real-estate:lease_agreement': 'realestate-rental',
  'real-estate:inspection_report': 'realestate-deed',
  'real-estate:renovation_permit': 'construction-permit',
};

/** Lookup a product slug for a (segment, starter) pair, or
 *  `undefined` if the starter has no dedicated product. */
export function lookupStarterProduct(
  segmentId: string,
  starterId: string,
): string | undefined {
  return STARTER_TO_PRODUCT[`${segmentId}:${starterId}`];
}

/** Look up a prefill by slug. Returns `undefined` for unknown slugs
 *  so the caller can fall back to an empty Spark Step 1. */
export function lookupProductPrefill(slug: string): SparkProductPrefill | undefined {
  return PRODUCT_PREFILLS[slug];
}
